import * as creditDecisionActionCreators from '@makemydeal/dr-dash-store/actions/creditDecisionActionCreators';
import * as creditDecisionSelectors from '@makemydeal/dr-dash-store/selectors/creditDecisionSelectors';
import * as navigationHooks from '@makemydeal/dr-dash-store/hooks/navigation';
import * as offerSelectors from '@makemydeal/dr-dash-store/selectors/offerSelectors';
import { RouteValues } from '@makemydeal/dr-dash-store/routeConstants';
import { useDispatch, useSelector } from 'react-redux';
import {
    CardContainer,
    DescriptionInfo,
    FinancingContainer,
    FinancingTitle,
    LayoutContainer,
    LenderDecisionContainer
} from './LenderDecisionInfo.style';
import { LenderList } from './LenderList';
import { TitleSeparator } from './CreditDecision.style';
import LenderDecisionFooter from './LenderDecisionFooter';
import FniCreditDecisionWidget from './FniCreditDecisionWidget';
import * as fullLenderDecisionsSelectors from '@makemydeal/dr-dash-store/selectors/fullLenderDecisionsSelectors';
import { useState } from 'react';
import { offerSessionUtils } from '@makemydeal/dr-shared-ui-utils';
import { dealerSelectors, featureToggleSelectors } from '@makemydeal/dr-shared-store';
import type { CreditDecision } from '@makemydeal/dr-dash-types';
import { DealerDecisionStatus } from '@makemydeal/dr-dash-types';

export const clearSelectedDecisionActiveState = (prev: CreditDecision | null): CreditDecision | null =>
    prev?.lenderId ? { ...prev, isActive: false } : prev;

const LenderDecisionInfo = () => {
    const dispatch = useDispatch();
    const dealRefId = useSelector(offerSelectors.getDealRefId);
    const lenderCreditDecisions = useSelector(creditDecisionSelectors.getLenderCreditDecisions);
    const fullLenderDecisions = useSelector(fullLenderDecisionsSelectors.getFullLenderDecisionsAsCreditDecisionList);
    const isFullLenderDecisionsEnabled = useSelector(featureToggleSelectors.isEnableMVLenderDecisions);
    const isFldFinanceMethodSameAsCurrentOfferType = useSelector(
        fullLenderDecisionsSelectors.isFldFinanceMethodSameAsCurrentOfferType
    );
    const routeOnePartyId = useSelector(dealerSelectors.getRouteOnePartyId);
    const creditProviderIsRouteOne = useSelector(dealerSelectors.isCreditProviderRouteOne);

    const [showLenderDecisionFooter, setShowLenderDecisionFooter] = useState(false);
    const [selectedLenderId, setSelectedLenderId] = useState('');
    const [selectedDecision, setSelectedDecision] = useState<CreditDecision | null>(null);

    const environment = offerSessionUtils.getEnvironmentType() === 'prod' ? 'prod' : 'uat';
    const lenderDecisions =
        isFullLenderDecisionsEnabled && isFldFinanceMethodSameAsCurrentOfferType ? fullLenderDecisions : lenderCreditDecisions;

    const filteredLenderDecisions = lenderDecisions.filter((d) => {
        return d.approvalStatus !== DealerDecisionStatus.NOT_SUBMITTED;
    });

    const goToDashboard = navigationHooks.useNavigate(RouteValues.DASHBOARD);

    const showFooter = () => {
        setShowLenderDecisionFooter(!showLenderDecisionFooter);
    };

    const hideFooterAndNavigate = () => {
        setShowLenderDecisionFooter(!showLenderDecisionFooter);
        goToDashboard();

        setSelectedDecision(clearSelectedDecisionActiveState);
    };

    const applySelectedCreditDecisionAndNavigate = () => {
        if (selectedDecision) {
            dispatch(creditDecisionActionCreators.updateSelectedCreditDecision(selectedDecision));
        }

        goToDashboard();
        setSelectedDecision(clearSelectedDecisionActiveState);
    };

    const handleSelectedLenderId = (lenderId: string) => {
        if (selectedLenderId === lenderId) {
            setSelectedLenderId('');
            setShowLenderDecisionFooter(false);
        } else {
            setSelectedLenderId(lenderId);
            setShowLenderDecisionFooter(true);
        }
    };

    return (
        <LenderDecisionContainer data-testid="lender-decision-layout">
            <TitleSeparator />
            <DescriptionInfo>
                <FinancingContainer>
                    <FinancingTitle>Select A Decision Below To View The Detailed Terms</FinancingTitle>
                </FinancingContainer>
            </DescriptionInfo>

            <LayoutContainer>
                <CardContainer>
                    <span className="lenderDecisionTitle">Lender Decisions ({filteredLenderDecisions.length})</span>
                    <LenderList
                        onCardSelected={handleSelectedLenderId}
                        lenderDecisions={filteredLenderDecisions}
                        sortOrder={['approvalStatus']}
                        showFooter={showFooter}
                        setSelectedDecision={setSelectedDecision}
                    />
                </CardContainer>
                {selectedLenderId && (
                    <div className="lenderDecisionWidget" id="ldWidget">
                        <FniCreditDecisionWidget
                            key={selectedLenderId}
                            dealRefId={dealRefId}
                            selectedLenderId={selectedLenderId}
                            environment={environment}
                            routeOnePartyId={routeOnePartyId}
                            creditProviderIsRouteOne={creditProviderIsRouteOne}
                        />
                    </div>
                )}
            </LayoutContainer>
            {showLenderDecisionFooter && (
                <LenderDecisionFooter
                    applySelectedCreditDecisionAndNavigate={applySelectedCreditDecisionAndNavigate}
                    hideFooter={hideFooterAndNavigate}
                />
            )}
        </LenderDecisionContainer>
    );
};

export default LenderDecisionInfo;
